/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Link } from 'react-router';

import { findIndex, isEmpty } from 'lodash';

import useRouter from 'js/lib/useRouter';

import { Typography } from '@coursera/cds-core';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import type { SubItem } from 'bundles/teach-partner-2018/constants/InstitutionNavigationItems';

import NavigationSubItem from './NavigationSubItem';

type Props = {
  name: string;
  subItems?: Array<SubItem>;
  target: string;
};

const styles = {
  default: () => css`
    padding: var(--cds-spacing-150) 0 var(--cds-spacing-150) var(--cds-spacing-300);
    font-size: 1rem;
    border-left: 6px solid transparent;

    &:hover {
      background-color: var(--cds-color-neutral-background-primary);
      border-left-color: var(--cds-color-blue-50);
    }
  `,
  isSelected: () => css`
    font-weight: bold;
    background-color: var(--cds-color-neutral-background-primary);
    border-left-color: var(--cds-color-blue-700);

    &:hover {
      border-left-color: var(--cds-color-blue-700);
    }
  `,
};

const NavigationItem = ({ name, target, subItems }: Props) => {
  const router = useRouter();

  const isActive = (routerTarget: string) => {
    return router.isActive({
      [routerTarget.charAt(0) === '/' ? 'pathname' : 'name']: routerTarget,
      params: router.params,
      query: router.location.query,
    });
  };

  const activeIndex = Math.max(
    0,
    findIndex(subItems, (subItem) => {
      return subItem && !subItem.isHidden && isActive(subItem.target);
    })
  );

  const findActiveSubItem = (items: SubItem[]): boolean => {
    return items?.some((subItem) => {
      const isSubItemActive = isActive(subItem.target);
      if (isSubItemActive) {
        return isSubItemActive;
      } else if (subItem.subItems) {
        return findActiveSubItem(subItem.subItems);
      } else {
        return false;
      }
    });
  };

  const isChildSelected = subItems && findActiveSubItem(subItems);
  const isSelected = isActive(target) && !isChildSelected;
  const isExpanded = isSelected || isChildSelected;

  return (
    <li css={{ cursor: 'pointer', position: 'relative' }}>
      <Link
        className="nostyle"
        to={{
          name: target,
          params: router.params,
          query: router.location.query,
        }}
      >
        <TrackedDiv
          trackClicks
          trackingName="menu_item"
          data={{ entry: name }}
          css={[styles.default, isSelected && styles.isSelected]}
          data-e2e={`Tracked_Link~${name}`}
          data-pendo-guide={`MenuItem~${name}`}
        >
          <Typography variant={isExpanded ? 'h3semibold' : 'body1'} component="p">
            {name}
          </Typography>
        </TrackedDiv>
      </Link>
      {isExpanded && !isEmpty(subItems) && (
        <ul
          css={{
            listStyleType: 'none',
            margin: 0,
            padding: 0,
          }}
          data-testid="navigationSubItems"
        >
          {subItems?.map((subItem, index) => (
            <div key={subItem.target} data-testid={index === activeIndex ? 'activeNavItem' : null}>
              <NavigationSubItem {...subItem} isSelected={index === activeIndex} />
            </div>
          ))}
        </ul>
      )}
    </li>
  );
};

export default NavigationItem;
